import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Trophy, Plus, Trash2, Target } from '../../components/icons';
import { fetchMilestones, createMilestone, deleteMilestone, fetchGoals, type Milestone, type Goal } from '../../lib/progress';
import { LoadingSpinner } from '../../components/LoadingSpinner';

function groupByMonth(milestones: Milestone[]): Record<string, Milestone[]> {
  return milestones.reduce((acc, m) => {
    const key = new Date(m.achieved_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {} as Record<string, Milestone[]>);
}

export function MilestonesView() {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', achieved_at: new Date().toISOString().split('T')[0], goal_id: '' });

  useEffect(() => {
    if (!user) return;
    Promise.all([
      fetchMilestones(user.id),
      fetchGoals(user.id),
    ]).then(([m, g]) => {
      setMilestones(m);
      setGoals(g);
    }).finally(() => setIsLoading(false));
  }, [user]);

  const goalMap = new Map(goals.map(g => [g.id, g]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim()) return;
    const payload: Partial<Milestone> = {
      title: form.title,
      description: form.description || undefined,
      achieved_at: form.achieved_at,
    };
    if (form.goal_id) {
      payload.goal_id = form.goal_id;
    }
    const created = await createMilestone(user.id, payload);
    if (created) {
      setMilestones(prev => {
        const next = [created, ...prev];
        return next.sort((a, b) => new Date(b.achieved_at).getTime() - new Date(a.achieved_at).getTime());
      });
    }
    setShowForm(false);
    setForm({ title: '', description: '', achieved_at: new Date().toISOString().split('T')[0], goal_id: '' });
  };

  const handleDelete = async (id: string) => {
    await deleteMilestone(id);
    setMilestones(prev => prev.filter(m => m.id !== id));
  };

  const grouped = groupByMonth(milestones);
  const monthKeys = Object.keys(grouped);
  const linkedCount = milestones.filter(m => m.goal_id).length;

  if (isLoading) return <LoadingSpinner message="Loading milestones..." />;

  return (
    <div className="p-6 md:p-8 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
            <Trophy className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Milestones</h1>
            <p className="text-sm text-chocolate-500">
              {milestones.length} achievements
              {linkedCount > 0 && <span className="text-sage-600"> · {linkedCount} linked to goals</span>}
            </p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="freud-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Milestone
        </button>
      </div>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">New Milestone</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="freud-label">Title *</label>
              <input className="freud-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="What did you achieve?" required />
            </div>
            <div>
              <label className="freud-label">Description</label>
              <textarea className="freud-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="More details..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Achieved On</label>
                <input type="date" className="freud-input" value={form.achieved_at} onChange={e => setForm(p => ({ ...p, achieved_at: e.target.value }))} />
              </div>
              <div>
                <label className="freud-label">Linked Goal</label>
                <select
                  className="freud-input"
                  value={form.goal_id}
                  onChange={e => setForm(p => ({ ...p, goal_id: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {goals.map(g => (
                    <option key={g.id} value={g.id}>{g.title}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">Save Milestone</button>
              <button type="button" onClick={() => setShowForm(false)} className="freud-btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {milestones.length === 0 && !showForm ? (
        <div className="freud-card-elevated p-12 text-center">
          <Trophy className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">No milestones yet. Celebrate your first win!</p>
        </div>
      ) : (
        <div className="space-y-8">
          {monthKeys.map(month => (
            <div key={month}>
              <p className="text-xs font-bold uppercase tracking-widest text-chocolate-400 mb-4">{month}</p>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-cream-300" />
                <div className="space-y-4">
                  {grouped[month].map(milestone => (
                    <div key={milestone.id} className="flex gap-4 items-start pl-10 relative">
                      <div className="absolute left-2 top-2.5 w-4 h-4 bg-yellow-400 rounded-full ring-2 ring-cream-50 flex-shrink-0 z-10 flex items-center justify-center">
                        <Trophy className="w-2.5 h-2.5 text-white" />
                      </div>
                      <div className="freud-card-elevated p-4 flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-chocolate-900">{milestone.title}</p>
                            {milestone.description && <p className="text-sm text-chocolate-500 mt-0.5">{milestone.description}</p>}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <p className="text-xs text-chocolate-400">{new Date(milestone.achieved_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                              {milestone.goal_id && goalMap.has(milestone.goal_id) && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-sage-600 bg-sage-50 px-1.5 py-0.5 rounded">
                                  <Target className="w-3 h-3" />
                                  {goalMap.get(milestone.goal_id)!.title}
                                </span>
                              )}
                            </div>
                          </div>
                          <button onClick={() => handleDelete(milestone.id)} className="p-1.5 rounded-lg text-chocolate-400 hover:bg-red-50 hover:text-red-500 transition-colors flex-shrink-0 ml-2">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
